// react-frontend/src/hooks/useSaveManager.js
import { useState, useEffect, useCallback, useRef } from 'react';
import saveManager from '../../../utils/saveManager';

export const useSaveManager = (username, erdState, getClassContext) => {
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const saveTimeoutRef = useRef(null);

  // Track ERD state changes to detect unsaved changes
  const erdStateRef = useRef(erdState);

  useEffect(() => {
    const hasChanges = JSON.stringify(erdState) !== JSON.stringify(erdStateRef.current);
    if (hasChanges && saveStatus === 'saved') {
      setUnsavedChanges(true);
      setSaveStatus('unsaved');
    }
    erdStateRef.current = erdState;
  }, [erdState, saveStatus]);

  // Manual save function
  const manualSave = useCallback(async () => {
    if (!username) return false;

    // Check if environment is 'None' and prevent save
    if (erdState.selectedEnvironment === 'None') {
      setSaveError('Cannot save when environment is set to "None"');
      return false;
    }

    setSaveStatus('saving');
    setSaveError('');

    try {
      const success = await saveManager.handleSave('manual_save', erdState, username, {}, getClassContext);

      if (success) {
        setSaveStatus('saved');
        setLastSaved(Date.now());
        setUnsavedChanges(false);
        erdStateRef.current = { ...erdState };
        return true;
      } else {
        throw new Error('Save operation failed');
      }
    } catch (error) {
      console.error('Manual save error:', error);
      setSaveStatus('error');
      setSaveError(error.message || 'Save failed');
      return false;
    }
  }, [username, erdState, getClassContext]);

  // Event-driven save function
  const eventSave = useCallback(async (eventType, customData = {}) => {
    if (!username || !eventType) return false;

    // Check if environment is 'None' and prevent save (except for environment change events)
    if (erdState.selectedEnvironment === 'None' && eventType !== 'environment_changed') {
      console.log('EventSave: Skipping save - environment is set to None');
      return false;
    }

    // Set status based on event type
    const isImmediate = ['entity_added', 'entity_deleted', 'relationship_added', 'relationship_deleted', 'erd_generated'].includes(eventType);

    if (isImmediate && erdState.selectedEnvironment !== 'None') {
      setSaveStatus('saving');
    } else if (erdState.selectedEnvironment !== 'None') {
      setSaveStatus('pending');
    }

    setSaveError('');

    try {
      const success = await saveManager.handleSave(eventType, erdState, username, customData, getClassContext);

      if (success || !isImmediate) { // Debounced saves return true immediately
        if (isImmediate && erdState.selectedEnvironment !== 'None') {
          setSaveStatus('saved');
          setLastSaved(Date.now());
          setUnsavedChanges(false);
          erdStateRef.current = { ...erdState };
        }
        return true;
      } else {
        throw new Error('Save operation failed');
      }
    } catch (error) {
      console.error('Event save error:', error);
      if (erdState.selectedEnvironment !== 'None') {
        setSaveStatus('error');
        setSaveError(error.message || 'Save failed');
      }
      return false;
    }
  }, [username, erdState, getClassContext]);

  // Auto-save functionality
  useEffect(() => {
    if (!username || !unsavedChanges || erdState.selectedEnvironment === 'None') return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      eventSave('auto_save');
    }, 5000); // Auto-save after 5 seconds of inactivity

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [unsavedChanges, username, eventSave, erdState.selectedEnvironment]);

  // Listen for successful debounced saves
  useEffect(() => {
    const handleDebouncedSaveComplete = () => {
      setSaveStatus('saved');
      setLastSaved(Date.now());
      setUnsavedChanges(false);
      erdStateRef.current = { ...erdState };
    };

    // This would be called by the SaveManager when debounced saves complete
    // For now, we'll use a simple timeout to simulate this
    if (saveStatus === 'pending') {
      const timer = setTimeout(() => {
        handleDebouncedSaveComplete();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [saveStatus, erdState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Load saved work
  const loadSavedWork = useCallback(async (workData) => {
    if (!workData || !workData.erdState) {
      console.error('Invalid work data provided to loadSavedWork');
      return false;
    }

    try {
      // Update ERD state with loaded data
      const loadedState = workData.erdState;

      // This would typically be handled by the parent component
      // that manages the ERD state, but we can return the data
      // for the parent to apply

      setSaveStatus('saved');
      setLastSaved(workData.metadata?.timestamp || Date.now());
      setUnsavedChanges(false);
      erdStateRef.current = loadedState;

      return loadedState;
    } catch (error) {
      console.error('Error loading saved work:', error);
      setSaveError('Failed to load saved work');
      return false;
    }
  }, []);

  // Get save manager queue status
  const getQueueStatus = useCallback(() => {
    return saveManager.getQueueStatus();
  }, []);

  return {
    // Status
    saveStatus,
    lastSaved,
    saveError,
    unsavedChanges,

    // Actions
    manualSave,
    eventSave,
    loadSavedWork,

    // Utilities
    getQueueStatus
  };
};